import React from 'react';

const RealWorldExampleSection = () => {
  return (
    <section className="py-20 bg-gray-100 relative overflow-hidden">
      {/* Decorative Background Elements */}
      <div className="absolute top-20 right-0 w-96 h-96 bg-teal-100 rounded-full opacity-20 blur-3xl pointer-events-none"></div>
      <div className="absolute bottom-20 left-0 w-96 h-96 bg-blue-100 rounded-full opacity-20 blur-3xl pointer-events-none"></div>

      <div className="max-w-7xl mx-auto px-4 relative z-10">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            Ein Beispiel aus dem Alltag
          </h2>
          <p className="text-xl text-gray-700 max-w-3xl mx-auto">
            Wie Anna mit IDA ihre Schicht strukturiert, Zeit spart und rechtssicher arbeitet.
          </p>
          <div className="w-24 h-1 bg-gradient-to-r from-teal-700 to-blue-500 mx-auto rounded-full mt-6"></div>
        </div>

        {/* Main Content Container */}
        <div className="flex flex-col lg:flex-row gap-8 items-start">

          {/* Left Side - Video Container */}
          <div className="w-full lg:w-[40%] flex justify-center lg:justify-start">
            <div className="relative group w-full">
              {/* Video Container with Enhanced Styling */}
              <div className="relative aspect-square w-full overflow-hidden rounded-3xl shadow-2xl border-4 border-white">
                {/* Gradient Border Effect */}
                <div className="absolute inset-0 bg-gradient-to-br from-teal-400 via-blue-400 to-teal-600 opacity-0 group-hover:opacity-10 transition-opacity duration-500 rounded-3xl"></div>

                <video
                  className="w-full h-full object-cover relative z-10"
                  autoPlay
                  muted
                  loop
                  playsInline
                  preload="metadata"
                  onLoadedMetadata={(e) => {
                    // Start playing when scrolled into view
                    const observer = new IntersectionObserver(
                      (entries) => {
                        entries.forEach((entry) => {
                          if (entry.isIntersecting) {
                            e.target.play();
                          } else {
                            e.target.pause();
                          }
                        });
                      },
                      { threshold: 0.5 }
                    );
                    observer.observe(e.target);
                  }}
                >
                  <source src="/videos/20251110_0936_Loop Video_loop_01k9pehxrpe3386tq5yxdf4v2c.mp4" type="video/mp4" />
                  Ihr Browser unterstützt das Video-Tag nicht.
                </video>
              </div>

              {/* Decorative Label */}
              <div className="absolute -bottom-4 left-1/2 transform -translate-x-1/2 bg-gradient-to-r from-teal-800 to-blue-600 text-white px-6 py-2 rounded-full shadow-lg text-sm font-semibold whitespace-nowrap z-20">
                Anna's Arbeitsablauf
              </div>
            </div>
          </div>

          {/* Right Side - Benefits Grid */}
          <div className="w-full lg:w-[60%]">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">

              {/* Benefit 1: Strukturiertes Arbeiten */}
              <div className="shadow-lg border-2 border-gray-200 bg-gradient-to-br from-white to-gray-50 rounded-xl p-6">
                <div className="flex items-start gap-4 group">
                  <div className="w-8 h-8 bg-gradient-to-br from-teal-100 to-teal-200 rounded-lg flex items-center justify-center text-teal-800 shadow-md flex-shrink-0 mt-1 group-hover:bg-teal-200 transition-colors">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
                    </svg>
                  </div>
                  <div>
                    <h4 className="font-bold text-gray-900 mb-1 mt-1">Strukturiertes Arbeiten</h4>
                    <p className="text-gray-700 leading-relaxed">
                      Klare Checklisten führen durch alle Aufgaben.
                    </p>
                  </div>
                </div>
              </div>

              {/* Benefit 2: Zeitersparnis */}
              <div className="shadow-lg border-2 border-gray-200 bg-gradient-to-br from-white to-gray-50 rounded-xl p-6">
                <div className="flex items-start gap-4 group">
                  <div className="w-8 h-8 bg-gradient-to-br from-teal-100 to-teal-200 rounded-lg flex items-center justify-center text-teal-800 shadow-md flex-shrink-0 mt-1 group-hover:bg-teal-200 transition-colors">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <div>
                    <h4 className="font-bold text-gray-900 mb-1 mt-1">Zeitersparnis</h4>
                    <p className="text-gray-700 leading-relaxed">
                      Automatisierte Prozesse reduzieren Aufwand.
                    </p>
                  </div>
                </div>
              </div>

              {/* Benefit 3: Sicherheit */}
              <div className="shadow-lg border-2 border-gray-200 bg-gradient-to-br from-white to-gray-50 rounded-xl p-6">
                <div className="flex items-start gap-4 group">
                  <div className="w-8 h-8 bg-gradient-to-br from-teal-100 to-teal-200 rounded-lg flex items-center justify-center text-teal-800 shadow-md flex-shrink-0 mt-1 group-hover:bg-teal-200 transition-colors">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                  </div>
                  <div>
                    <h4 className="font-bold text-gray-900 mb-1 mt-1">Sicherheit</h4>
                    <p className="text-gray-700 leading-relaxed">
                      Vollständige Dokumentation aller Tätigkeiten.
                    </p>
                  </div>
                </div>
              </div>

              {/* Benefit 4: Rechtssicherheit */}
              <div className="shadow-lg border-2 border-gray-200 bg-gradient-to-br from-white to-gray-50 rounded-xl p-6">
                <div className="flex items-start gap-4 group">
                  <div className="w-8 h-8 bg-gradient-to-br from-teal-100 to-teal-200 rounded-lg flex items-center justify-center text-teal-800 shadow-md flex-shrink-0 mt-1 group-hover:bg-teal-200 transition-colors">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                    </svg>
                  </div>
                  <div>
                    <h4 className="font-bold text-gray-900 mb-1 mt-1">Rechtssicherheit</h4>
                    <p className="text-gray-700 leading-relaxed">
                      Compliance-konforme Abläufe.
                    </p>
                  </div>
                </div>
              </div>

            </div>

            {/* Additional Context Box */}
            <div className="mt-8 bg-gradient-to-r from-teal-50 to-blue-50 rounded-2xl p-6 border-2 border-teal-700 shadow-lg">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 bg-teal-700 rounded-full flex items-center justify-center flex-shrink-0">
                  <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                  </svg>
                </div>
                <div>
                  <h4 className="font-bold text-gray-900 mb-2">Alles in einem System</h4>
                  <p className="text-gray-700 leading-relaxed">
                    Keine separaten Tools, keine doppelte Dokumentation. IDA integriert alle wichtigen Funktionen direkt in den Arbeitsablauf und macht Compliance zum natürlichen Teil der Schicht.
                  </p>
                </div>
              </div>
            </div>
          </div>

        </div>
      </div>
    </section>
  );
};

export default RealWorldExampleSection;
